import clsx from "clsx";
import {
  MdAdd,
  MdEdit,
  MdMoreHoriz,
  MdOutlineAttachment,
  MdOutlineCompareArrows,
  MdOutlineCreditCard,
  MdOutlineQueryStats,
  MdOutlineSwapVert,
} from "react-icons/md";
import { ETransactionType } from "src/helpers/constants";
import { motion } from "framer-motion";
import { memo, useMemo } from "react";
import useMobile from "src/hooks/useMobile";
import { useTranslation } from "react-i18next";
import ShinyCard from "../BankCard/ShinyCard";
import {
  getTotalByTransactionType,
  truncateWithEllipsis,
} from "src/helpers/utils";
import { useLocation, useNavigate } from "react-router-dom";
import { chain } from "lodash";
import useAllTransactions from "src/hooks/useAllTransactions";
import useCurrencies from "src/hooks/useCurrencies";
import useCurrentUser from "src/hooks/useCurrentUser";
import useModalsStore from "src/store/modalsStore";
import temporalStore from "src/store/temporalStore";
import useColors from "src/hooks/useColors";
import { ROUTES } from "src/helpers/routes";
import ButtonGroup from "../ButtonGroup/ButtonGroup";
import SmartImage from "../SmartImage/SmartImage";
import CurrencyBadge from "../CurrencyBadge/CurrencyBadge";
import useUiPreferences from "src/store/useUiPreferences";
import { ICON_SIZES } from "src/constants/icons";
import { EButtonKind } from "../Button/Button.types";

interface IAccountCardV2 {
  account: any;
  animationIndex: number;
  redirectTo?: string;
}

const AccountCardV2: React.FC<IAccountCardV2> = ({
  account,
  animationIndex,
  redirectTo = "",
}) => {
  const enableAnimations = useUiPreferences(
    (state) => state.misc.enableComplexAnimations,
  );
  const { isMobile } = useMobile();
  const { getAccountColors } = useColors();
  const { setShowMoneyTransferModal, setShowEditAccountModal } =
    useModalsStore();
  const { setPreselectedAccountForTransfer } = temporalStore();
  const { getCurrencyDataByCode } = useCurrencies();
  const { getAccountById } = useCurrentUser();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { allActiveTransactions } = useAllTransactions();

  const defaultRedirectTo = chain(location.pathname)
    .split("/") // divide en partes
    .get(1, "") // obtiene el índice 1 o "" si no existe
    .thru((part) => (part ? `/${part}` : "")) // añade "/" si existe
    .value();

  const currencySymbol = useMemo(() => {
    return (
      getCurrencyDataByCode({
        code: getAccountById(account?.account)?.currency,
      })?.symbol_native || ""
    );
  }, []);

  const getAvailableMoney = useMemo(() => {
    const income = getTotalByTransactionType({
      type: ETransactionType.INCOME,
      transactions: allActiveTransactions,
      account: account?.account,
    });
    const expenses = getTotalByTransactionType({
      type: ETransactionType.EXPENSE,
      transactions: allActiveTransactions,
      account: account?.account,
    });

    return income - expenses;
  }, [allActiveTransactions]);

  const accountName = useMemo(() => {
    const prefix = account?.settings?.disabled ? "* " : "";

    return `${prefix}${truncateWithEllipsis({ text: account?.name, maxLen: 20 })}`;
  }, [account]);

  return (
    <>
      <ShinyCard>
        <motion.div
          className={clsx(["flex", "w-full"])}
          {...(enableAnimations && {
            initial: {
              opacity: 0,
            },
            animate: { opacity: 1, x: 0 },
            transition: {
              duration: 0.2,
              ease: "easeOut",
              delay: animationIndex * 0.1,
            },
          })}
        >
          <div
            className={clsx([
              "flex",
              "flex-row",
              "items-center",
              "w-full",
              "rounded",
              "md:gap-4",
              "gap-2",
              "cursor-pointer",
              "bg-gray-50/50 dark:bg-gray-900/50",
              "p-2",
              "md:p-2",
              "border",
              "border-blue-50",
              "dark:border-gray-800",
              "relative",
            ])}
            onClick={(e: any) => {
              e?.preventDefault?.();
              navigate(
                redirectTo || `${defaultRedirectTo}/${account?.account}`,
              );
            }}
            style={{
              ...getAccountColors(account?.account).string,
            }}
          >
            <div
              className={clsx([
                "flex",
                "flex-col",
                "md:flex-row",
                "gap-1",
                "items-start",
                "md:items-center",
                "w-full",
              ])}
            >
              <div className={clsx(["items-center", "justify-center", "flex"])}>
                {account?.settings?.picture ? (
                  <div
                    className={clsx([
                      "shrink-0",
                      "h-[32px]",
                      "w-[32px]",
                      "md:h-[48px]",
                      "md:w-[48px]",
                    ])}
                  >
                    <SmartImage
                      src={account?.settings?.picture?.thumbnailUrl}
                      alt="Account"
                      className={clsx([
                        "shrink-0",
                        "h-[32px]",
                        "w-[32px]",
                        "md:h-[48px]",
                        "md:w-[48px]",
                        "rounded",
                      ])}
                    />
                  </div>
                ) : (
                  <MdOutlineCreditCard
                    size={isMobile ? ICON_SIZES.MD : ICON_SIZES.LG}
                    className={clsx(["group-hover:animate-pulse"])}
                  />
                )}
              </div>
              <div
                className={clsx([
                  "flex",
                  "flex-col",
                  "flex-grow",
                  "md:items-start",
                  "items-start",
                ])}
              >
                <div
                  className={clsx([
                    "text-xs",
                    "flex",
                    "md:text-xs",
                    "text-center",
                    "md:text-left",
                  ])}
                >
                  {accountName}
                </div>
                <CurrencyBadge
                  value={getAvailableMoney}
                  currencySymbol={currencySymbol}
                  kind="NONE"
                />
              </div>
            </div>
            {/* {!isMobile && ( */}
            <div
              className={clsx([
                "absolute",
                "md:relative",
                isMobile && ["top-0", "right-0"],
              ])}
            >
              <ButtonGroup
                buttons={[
                  {
                    className: clsx([
                      isMobile && [
                        "!bg-transparent",
                        "!border-none",
                        "hover:!text-current",
                        "!shadow-none",
                        "!text-current",
                      ],
                    ]),
                    icon: (
                      <MdMoreHoriz
                        size={ICON_SIZES.SM}
                        style={{
                          ...(isMobile
                            ? {
                                color: getAccountColors(account?.account).string
                                  .color,
                              }
                            : {}),
                        }}
                      />
                    ),
                    options: [
                      {
                        text: t("misc:menu.new_transaction"),
                        icon: <MdAdd size={ICON_SIZES.SM} />,
                        onClick: (e: any) => {
                          e?.preventDefault?.();
                          navigate(ROUTES.TRANSACTION_DETAIL(account?.account));
                        },
                        kind: EButtonKind.NORMAL,
                      },
                      {
                        text: t("misc:menu.transactions"),
                        icon: <MdOutlineSwapVert size={ICON_SIZES.SM} />,
                        onClick: (e: any) => {
                          e?.preventDefault?.();
                          navigate(ROUTES.TRANSACTIONS(account?.account));
                        },
                        kind: EButtonKind.NORMAL,
                      },
                      {
                        text: t("misc:menu.stats"),
                        icon: <MdOutlineQueryStats size={ICON_SIZES.SM} />,
                        onClick: (e: any) => {
                          e?.preventDefault?.();
                          navigate(ROUTES.REPORTS(account?.account));
                        },
                        kind: EButtonKind.NORMAL,
                      },
                      {
                        text: t("misc:menu.files"),
                        icon: <MdOutlineAttachment size={ICON_SIZES.SM} />,
                        onClick: (e: any) => {
                          e?.preventDefault?.();
                          navigate(ROUTES.FILES(account?.account));
                        },
                        kind: EButtonKind.NORMAL,
                      },
                      {
                        text: t("common:menus.transfer_funds"),
                        icon: <MdOutlineCompareArrows size={ICON_SIZES.SM} />,
                        onClick: (e: any) => {
                          e?.preventDefault?.();
                          setPreselectedAccountForTransfer(account?.account);
                          setShowMoneyTransferModal(true);
                        },
                        kind: EButtonKind.NORMAL,
                      },
                      {
                        text: t("common:tooltips.edit"),
                        icon: <MdEdit size={ICON_SIZES.SM} />,
                        onClick: (e: any) => {
                          e?.preventDefault?.();
                          setShowEditAccountModal(true, {
                            accountId: account?._id,
                            mode: "edit",
                            initialValues: {
                              account: account?.account,
                              name: account?.name,
                              bank: account?.bank,
                              currency: account?.currency,
                              bgColor: account?.settings?.bg,
                              textColor: account?.settings?.color,
                              isDisabled: account?.settings?.disabled,
                              number: account?.number,
                              image: account?.settings?.picture || null,
                            },
                          });
                        },
                        kind: EButtonKind.NORMAL,
                      },
                    ],
                  },
                ]}
              />
            </div>
          </div>
        </motion.div>
      </ShinyCard>
    </>
  );
};

export default memo(AccountCardV2);
